import { Autocomplete, Checkbox, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { createGstrClient, getClients } from "api/services/clients/gstr-clients";
import DrawerWrapper from "components/DrawerWrapper";
import LoadingButton from "components/LoadingButton";
import { snack } from "components/toast";
import { gstReturnsCategories } from "data/constants";
import _ from "lodash";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";

interface StateProps {
  client: any[];
  gstNumber: string;
  registrationType: any;
  syncAll: boolean;
}

let initialState: StateProps = {
  client: [],
  gstNumber: "",
  registrationType: null,
  syncAll: false,
};

function AddGstRecord({ open, setOpen, title }) {
  const [state, setState] = useState<StateProps>(_.cloneDeep(initialState));
  const [clients, setClients] = useState<any[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading: loading }: ResType = useQuery(
    ["clients", { registrationType: state.registrationType?.value }],
    getClients,
    {
      onSuccess: (res: any) => {
        const clientsData = res?.data;
        setState((prv) => ({ ...prv, client: [] }));
        setClients(clientsData);
      },
      enabled: !!state.registrationType,
    }
  );

  const { mutate, isLoading } = useMutation(createGstrClient, {
    onSuccess: () => {
      snack.success(`${title} successfully`);
      queryClient.invalidateQueries("clients-gst-returns");
      setState(_.cloneDeep(initialState));
      setOpen(false);
    },
    onError: (err: any) => {
      snack.error(err.response.data.message);
    },
  });

  const getGstReturn = async (e: any) => {
    e.preventDefault();
    if (!state.registrationType?.value)
      return snack.error("Select Registration Type");
    if (!state.client.length) return snack.error("Select Atleast One Client");

    mutate({
      clients: state.client.map((c) => c.id),
      registrationType: state.registrationType.value,
      syncAll: state.syncAll,
    });
  };

  const handleSyncAll = () => {
    setState((prv) => ({ ...prv, syncAll: !state.syncAll }));
  };

  return (
    <DrawerWrapper
      open={open}
      setOpen={() => {
        setOpen(false);
        setState(_.cloneDeep(initialState));
      }}
      title={"Add Record for GST Returns"}
    >
      <Box>
        <form ref={formRef} onSubmit={getGstReturn}>
          <Typography variant="h6" color="rgb(95, 33, 32)">
            Note: For a seamless, smooth, and error-free sync of your Client's
            GST Returns data, verifying their GSTIN upfront is highly
            recommended in the profile section. This preliminary step serves as
            a data integrity check, guaranteeing that the retrieved information
            pertains to the correct taxpayer.
          </Typography>

          {/* Registration Type */}
          <Autocomplete
            size="small"
            sx={{ mt: 3 }}
            onChange={(_, value) => {
              setState({
                ...state,
                registrationType: value,
              });
            }}
            value={state.registrationType}
            options={gstReturnsCategories || []}
            getOptionLabel={(option: any) => option.label}
            fullWidth
            renderInput={(params) => (
              <TextField {...params} size="small" label="Registration Type" />
            )}
          />

          {state.registrationType && (
            <>
              {/* Clients with Checkbox + Select All */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                id="clients-autocomplete"
                value={state.client}
                options={[{ id: "all", displayName: "Select All" }, ...clients]}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                getOptionLabel={(option: any) => option.displayName}
                onChange={(_, value: any[]) => {
                  // Handle Select All
                  if (value.some((v) => v.id === "all")) {
                    if (state.client.length === clients.length) {
                      // If already all selected, clear all
                      setState((prv) => ({ ...prv, client: [] }));
                    } else {
                      // Select all clients
                      setState((prv) => ({ ...prv, client: clients }));
                    }
                  } else {
                    setState((prv) => ({ ...prv, client: value }));
                  }
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={
                          option.id === "all"
                            ? state.client.length === clients.length &&
                            clients.length > 0
                            : selected
                        }
                      />
                      {option.displayName}
                    </Box>
                    {option.id !== "all" && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: "10px",
                          px: 1,
                          py: 0.2,
                          ml: 1,
                          bgcolor: option.gstTrackingRemoved ? "#fee2e2" : "#dcfce7",
                          color: option.gstTrackingRemoved ? "#b91c1c" : "#15803d",
                        }}
                      >
                        {option.gstTrackingRemoved ? "Existing Hidden" : "New"}
                      </Typography>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{ my: 2 }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    label="Select Clients"
                  />
                )}
              />
            </>
          )}

          {/* <Box>
            <FormControlLabel
              label="For a complete picture of your client's GST return history,
                             would you like to perform an initial 
                            synchronization of their data up to the current financial year?"
              control={
                <Switch
                  checked={state.syncAll}
                  onChange={handleSyncAll}
                  name="checkedSwitch"
                  color="primary"
                />
              }
            />
          </Box> */}

          {/* Submit */}
          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <LoadingButton
              loading={isLoading}
              fullWidth
              disabled={isLoading}
              type="submit"
              loadingColor="white"
              title="Submit"
              color="secondary"
            />
          </Box>
        </form>
      </Box>
    </DrawerWrapper>
  );
}

export default AddGstRecord;
